import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  CheckboxGrid,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getRnDischargeNoteForm,
  updateRnDischargeNoteForm,
} from "../../../../../services/patientService";
import {
  CommentsIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HelpingIcon,
  MedicalNotesIcon,
  NurseIcon,
  PhoneIcon,
  PillIcon,
  ShieldCheckIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { PATIENT_THEME } from "../../../patientTheme";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  RN_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  APPETITE_OPTIONS,
  CARDIO_RHYTHM_OPTIONS,
  DISCHARGED_TO_OPTIONS,
  DISCHARGE_REASON_OPTIONS,
  DISEASE_CONDITION_OPTIONS,
  EDEMA_OPTIONS,
  EDUCATION_TOPIC_OPTIONS,
  FALL_EDUCATION_OPTIONS,
  FALL_RISK_OPTIONS,
  FOLLOW_UP_OPTIONS,
  GI_STATUS_OPTIONS,
  GOAL_STATUS_OPTIONS,
  GU_STATUS_OPTIONS,
  LEARNER_OPTIONS,
  MENTAL_STATUS_OPTIONS,
  MOBILITY_OPTIONS,
  RESPIRATORY_OPTIONS,
  SKIN_INTEGRITY_OPTIONS,
  SN_GOAL_ROWS,
  UNDERSTANDING_OPTIONS,
  YES_NO_NA_OPTIONS,
  YES_NO_OPTIONS,
  emptyGoalStatuses,
  emptyRnDischargePayload,
  toggleInList,
  today,
} from "./rnDischargeNoteContent";
import NurseNotesCommonHeader from "./NurseNotesCommonHeader";
import ClinicalVitalsFields from "./ClinicalVitalsFields";
import WoundPresentSection from "./WoundPresentSection";
import { applyWoundAssessmentPayload } from "./woundAssessmentContent";
import {
  normalizeClinicalVitals,
  withCanonicalClinicalVitals,
} from "./clinicalVitals";
import {
  normalizeCommonHeaderState,
  resolveNurseNotesCommonHeader,
  withNormalizedCommonHeader,
} from "./nurseNotesCommonHeaderUtils";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "./nurseVisitCommon";

const DISCHARGE_DATE_MAX = new Date().toISOString().slice(0, 10);

/** Every payload field that holds a checkbox-group selection. */
const ARRAY_KEYS = [
  "dischargeReasons",
  "skinIntegrity",
  "incisionClosedWith",
  "mentalStatus",
  "respiratoryFindings",
  "cardioRhythm",
  "edema",
  "giAppetite",
  "giStatus",
  "guStatus",
  "finalMedListProvided",
  "finalEducationProvided",
  "diseaseConditions",
  "patientUnderstanding",
  "fallRisk",
  "fallPreventionEducation",
  "educationTopics",
  "learners",
  "dischargedTo",
  "followUpRecommendations",
];

const emptyValues = () => ({
  ...emptyRnDischargePayload(),
  dischargeDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const PillRadioGroup = ({
  label,
  name,
  options,
  value,
  canEdit,
  onChange,
  compact = false,
}) => (
  <div>
    {label ? (
      <SubHeading>{label}</SubHeading>
    ) : null}
    <div
      className="flex flex-wrap gap-1.5"
      role="radiogroup"
      aria-label={label || name}
    >
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={!canEdit}
            onClick={() => onChange(active ? "" : opt)}
            className={`${compact ? "h-7 px-2 text-[10px]" : "h-8 px-2.5 text-[11px]"} rounded font-medium border transition-colors disabled:opacity-60 whitespace-nowrap ${
              active
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-indigo-50/60 text-indigo-800 border-indigo-100 hover:bg-indigo-100"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

PillRadioGroup.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};


const RnDischargeNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const normalizeLoadedPayload = useCallback((loadedPayload) => {
    const base = emptyRnDischargePayload();
    const merged = { ...base, ...loadedPayload };

    ARRAY_KEYS.forEach((key) => {
      merged[key] = Array.isArray(loadedPayload[key]) ? loadedPayload[key] : [];
    });

    merged.commonHeader = normalizeCommonHeaderState(
      loadedPayload.commonHeader || base.commonHeader,
    );

    merged.goalStatuses = {
      ...emptyGoalStatuses(),
      ...(loadedPayload.goalStatuses && typeof loadedPayload.goalStatuses === "object"
        ? loadedPayload.goalStatuses
        : {}),
    };

    if (!merged.mobility) merged.mobility = "Independent";

    Object.assign(merged, normalizeClinicalVitals(merged));
    applyWoundAssessmentPayload(merged, merged, {
      mrn: patient?.mrn,
      clinicianName: [currentUser?.firstName, currentUser?.lastName]
        .filter(Boolean)
        .join(" "),
    });

    return merged;
  }, [currentUser?.firstName, currentUser?.lastName, patient?.mrn]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      setValues({
        ...normalizeLoadedPayload(loadedPayload),
        ...pickNurseVisitMeta(loadedPayload, data),
        dischargeDate: data.dischargeDate || loadedPayload.dischargeDate || today(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [normalizeLoadedPayload, patientName, resetSignatures],
  );

  /** Episode extras used once on first open / empty saved fields. */
  const buildEpisodePrefill = useCallback(() => {
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc = latestEpisode?.socDate || latestEpisode?.startDate || "";

    return {
      ...emptyRnDischargePayload(),
      primaryLanguage: patient?.language || "",
      socDate: soc ? formatDateForInput(soc) : "",
    };
  }, [patient]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveNurseNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName || draft?.formName || "Nurse Discharge Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            dischargeDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...buildEpisodePrefill(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getRnDischargeNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const episodePrefill = buildEpisodePrefill();
        const commonHeader = await resolveNurseNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            primaryLanguage:
              existingPayload.primaryLanguage ||
              episodePrefill.primaryLanguage ||
              "",
            socDate: existingPayload.socDate || episodePrefill.socDate || "",
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const setGoalStatus = (goal, status) => {
    setValues((prev) => ({
      ...prev,
      goalStatuses: {
        ...prev.goalStatuses,
        [goal]: prev.goalStatuses?.[goal] === status ? "" : status,
      },
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        RN_FORM_EDITOR_ROLES,
        "View only — only RN/LVN can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      dischargeDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...rnFields
    } = values;

    const nestedPayload = withCanonicalClinicalVitals(
      withNormalizedCommonHeader({
        ...rnFields,
        socDate: values.socDate ? formatDateForInput(values.socDate) : "",
        dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : "",
        goalStatuses: values.goalStatuses || emptyGoalStatuses(),
      }),
    );

    ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateRnDischargeNoteForm,
        filledByUserId,
        scheduleDate: values.dischargeDate
          ? formatDateForInput(values.dischargeDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Nurse Discharge Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Documentation for skilled nursing home health services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.dischargeDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="dischargeDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.dischargeDate) || ""}
                  onChange={(event) =>
                    setValue("dischargeDate", event.target.value)
                  }
                  max={DISCHARGE_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <NurseNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />


        <Section icon={CommentsIcon} title="7. Reason for Discharge">
          <CheckboxGrid
            options={DISCHARGE_REASON_OPTIONS}
            values={values.dischargeReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("dischargeReasons", opt)}
            columns={2}
          />
          <div className="mt-3">
            <Field label="Other (specify)">
              <input
                type="text"
                className={fieldClass}
                value={values.dischargeReasonOther}
                disabled={!canEdit}
                onChange={(e) => setValue("dischargeReasonOther", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={HeartPulseIcon} title="8. Clinical Vitals & Trends">
          <ClinicalVitalsFields
            values={values}
            canEdit={canEdit}
            onChange={(key, next) => setValue(key, next)}
          />
        </Section>

        <Section icon={NurseIcon} title="9. Skin / Wound Assessment (If Applicable)">
          <div className="space-y-3">
            <div>
              <SubHeading>Skin Integrity</SubHeading>
              <CheckboxGrid
                options={SKIN_INTEGRITY_OPTIONS}
                values={values.skinIntegrity}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("skinIntegrity", opt)}
                columns={2}
              />
            </div>

            <WoundPresentSection
              values={values}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(key, next) => setValue(key, next)}
              onToggleList={(key, opt) => toggleListValue(key, opt)}
            />
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="10. General Patient Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                Mental Status
              </div>
              <CheckboxGrid
                options={MENTAL_STATUS_OPTIONS}
                values={values.mentalStatus}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("mentalStatus", opt)}
                columns={1}
              />
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.mentalStatusComments}
                  disabled={!canEdit}
                  onChange={(e) => setValue("mentalStatusComments", e.target.value)}
                />
              </Field>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                Respiratory
              </div>
              <CheckboxGrid
                options={RESPIRATORY_OPTIONS}
                values={values.respiratoryFindings}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("respiratoryFindings", opt)}
                columns={1}
              />
              {values.respiratoryFindings.includes("Other") && (
                <Field label="Other (specify)">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.respiratoryOther}
                    disabled={!canEdit}
                    onChange={(e) => setValue("respiratoryOther", e.target.value)}
                  />
                </Field>
              )}
              <Field label="Respiratory Notes">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.respiratoryStatus}
                  disabled={!canEdit}
                  onChange={(e) => setValue("respiratoryStatus", e.target.value)}
                />
              </Field>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                Cardiovascular
              </div>
              <CheckboxGrid
                options={CARDIO_RHYTHM_OPTIONS}
                values={values.cardioRhythm}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("cardioRhythm", opt)}
                columns={1}
              />
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mt-1">
                Edema
              </div>
              <CheckboxGrid
                options={EDEMA_OPTIONS}
                values={values.edema}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("edema", opt)}
                columns={1}
              />
              {values.edema.includes("Present") && (
                <Field label="Location / Severity">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.edemaLocationSeverity}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("edemaLocationSeverity", e.target.value)
                    }
                  />
                </Field>
              )}
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                GI
              </div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                Appetite
              </div>
              <CheckboxGrid
                options={APPETITE_OPTIONS}
                values={values.giAppetite}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("giAppetite", opt)}
                columns={1}
              />
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mt-1">
                GI Status
              </div>
              <CheckboxGrid
                options={GI_STATUS_OPTIONS}
                values={values.giStatus}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("giStatus", opt)}
                columns={1}
              />
              {values.giStatus.includes("Other") && (
                <Field label="Other (specify)">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.giOther}
                    disabled={!canEdit}
                    onChange={(e) => setValue("giOther", e.target.value)}
                  />
                </Field>
              )}
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.giComments}
                  disabled={!canEdit}
                  onChange={(e) => setValue("giComments", e.target.value)}
                />
              </Field>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                GU
              </div>
              <CheckboxGrid
                options={GU_STATUS_OPTIONS}
                values={values.guStatus}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("guStatus", opt)}
                columns={1}
              />
              {values.guStatus.includes("Other") && (
                <Field label="Other (specify)">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.guOther}
                    disabled={!canEdit}
                    onChange={(e) => setValue("guOther", e.target.value)}
                  />
                </Field>
              )}
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.guComments}
                  disabled={!canEdit}
                  onChange={(e) => setValue("guComments", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section icon={PillIcon} title="11. Medication Reconciliation & Management">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <SubHeading>Final Medication List Provided?</SubHeading>
              <CheckboxGrid
                options={YES_NO_OPTIONS}
                values={values.finalMedListProvided}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("finalMedListProvided", opt)}
                columns={2}
              />
            </div>
            <div>
              <SubHeading>Medication Education Provided?</SubHeading>
              <CheckboxGrid
                options={YES_NO_OPTIONS}
                values={values.finalEducationProvided}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("finalEducationProvided", opt)}
                columns={2}
              />
            </div>
          </div>
        </Section>

        <Section icon={ShieldCheckIcon} title="12. Disease Management Assessment">
          <div className="space-y-3">
            <div>
              <SubHeading>Conditions</SubHeading>
              <CheckboxGrid
                options={DISEASE_CONDITION_OPTIONS}
                values={values.diseaseConditions}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("diseaseConditions", opt)}
                columns={2}
              />
            </div>
            <Field label="Other (specify)">
              <input
                type="text"
                className={fieldClass}
                value={values.diseaseConditionsOther}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("diseaseConditionsOther", e.target.value)
                }
              />
            </Field>
            <div>
              <SubHeading>Patient Understanding</SubHeading>
              <CheckboxGrid
                options={UNDERSTANDING_OPTIONS}
                values={values.patientUnderstanding}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("patientUnderstanding", opt)}
                columns={2}
              />
            </div>
          </div>
        </Section>

        <Section icon={TargetIcon} title="13. Functional Status Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3">
            <Field label="Mobility">
              <select
                className={fieldClass}
                value={values.mobility}
                disabled={!canEdit}
                onChange={(e) => setValue("mobility", e.target.value)}
              >
                {MOBILITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <div />
            <div>
              <SubHeading>Fall Risk</SubHeading>
              <CheckboxGrid
                options={FALL_RISK_OPTIONS}
                values={values.fallRisk}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("fallRisk", opt)}
                columns={3}
              />
            </div>
            <div>
              <SubHeading>Fall Prevention Education</SubHeading>
              <CheckboxGrid
                options={FALL_EDUCATION_OPTIONS}
                values={values.fallPreventionEducation}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("fallPreventionEducation", opt)}
                columns={2}
              />
            </div>
          </div>
        </Section>

        <Section icon={TargetIcon} title="14. Skilled Nursing Goals Status">
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[520px] text-left">
              <thead>
                <tr
                  className={`text-[10px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
                >
                  <th className="px-2 py-1.5 font-semibold">Goal</th>
                  <th className="px-2 py-1.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {SN_GOAL_ROWS.map((goal) => (
                  <tr key={goal} className="border-t border-slate-100">
                    <td className="px-2 py-2 text-[12px] text-slate-700 font-medium whitespace-nowrap">
                      {goal}
                    </td>
                    <td className="px-2 py-2">
                      <PillRadioGroup
                        name={`goal-${goal}`}
                        options={GOAL_STATUS_OPTIONS}
                        value={values.goalStatuses?.[goal] || ""}
                        canEdit={canEdit}
                        onChange={(status) => setGoalStatus(goal, status)}
                        compact
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <Field label="Goal Summary">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.goalSummary}
                disabled={!canEdit}
                placeholder="Summarize progress toward skilled nursing goals…"
                onChange={(e) => setValue("goalSummary", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={HelpingIcon} title="15. Patient / Caregiver Education">
          <div className="space-y-3">
            <div>
              <SubHeading>Topics Covered</SubHeading>
              <CheckboxGrid
                options={EDUCATION_TOPIC_OPTIONS}
                values={values.educationTopics}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("educationTopics", opt)}
                columns={2}
              />
            </div>
            <div>
              <SubHeading>Learner(s)</SubHeading>
              <CheckboxGrid
                options={LEARNER_OPTIONS}
                values={values.learners}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("learners", opt)}
                columns={2}
              />
            </div>
            <Field label="Learner Response / Understanding">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.educationResponse}
                disabled={!canEdit}
                onChange={(e) => setValue("educationResponse", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="16. Discharge Instructions & Plan">
          <div className="space-y-3">
            <div>
              <SubHeading>Discharged To</SubHeading>
              <CheckboxGrid
                options={DISCHARGED_TO_OPTIONS}
                values={values.dischargedTo}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("dischargedTo", opt)}
                columns={2}
              />
            </div>
            <Field label="Other (specify)">
              <input
                type="text"
                className={fieldClass}
                value={values.dischargedToOther}
                disabled={!canEdit}
                onChange={(e) => setValue("dischargedToOther", e.target.value)}
              />
            </Field>
            <div>
              <SubHeading>Follow-Up Recommendations</SubHeading>
              <CheckboxGrid
                options={FOLLOW_UP_OPTIONS}
                values={values.followUpRecommendations}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("followUpRecommendations", opt)}
                columns={2}
              />
            </div>
            <Field label="Additional Instructions">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.additionalInstructions}
                disabled={!canEdit}
                onChange={(e) => setValue("additionalInstructions", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={PhoneIcon} title="17. Physician / Care Team Communication">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-3">
            <PillRadioGroup
              label="Physician Notified?"
              name="physicianNotified"
              options={YES_NO_NA_OPTIONS}
              value={values.physicianNotified}
              canEdit={canEdit}
              onChange={(v) => setValue("physicianNotified", v)}
            />
            <PillRadioGroup
              label="RN Case Manager Notified?"
              name="rnCaseManagerNotified"
              options={YES_NO_NA_OPTIONS}
              value={values.rnCaseManagerNotified}
              canEdit={canEdit}
              onChange={(v) => setValue("rnCaseManagerNotified", v)}
            />
            <PillRadioGroup
              label="Therapy Team Notified?"
              name="therapyTeamNotified"
              options={YES_NO_NA_OPTIONS}
              value={values.therapyTeamNotified}
              canEdit={canEdit}
              onChange={(v) => setValue("therapyTeamNotified", v)}
            />
            <div className="sm:col-span-2 lg:col-span-3">
              <Field label="Communication Notes">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.communicationNotes}
                  disabled={!canEdit}
                  onChange={(e) => setValue("communicationNotes", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section icon={CommentsIcon} title="18. Final RN Discharge Summary Narrative">
          <Field label="Discharge Summary (required)">
            <textarea
              rows={6}
              className={textareaClass}
              value={values.dischargeSummary}
              disabled={!canEdit}
              placeholder="Summarize the episode of care, patient status at discharge, goals met, and recommendations…"
              onChange={(e) => setValue("dischargeSummary", e.target.value)}
            />
          </Field>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

RnDischargeNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default RnDischargeNoteForm;
